import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Table,
  Checkbox,
  SortDownIcon,
  SortUpIcon,
  BenefitType,
  IAutocompleteOption
} from 'edenred-ui';
import { useFormContext } from 'react-hook-form';
import { useTheme } from 'styled-components';

import { Pagination } from '@epi-shared/components';
import { TopupSorting } from '@epi-constants/topupSorting';
import { formatCurrencyDecimal } from '@epi-helpers/numeral';
import { getApplicationLanguageId } from '@epi-store/application/application.selectors';
import { IEmployeesTable } from '@epi-models/pages/TransferPage';
import { useSortEmployees } from '@epi-pages/LoadBenefit/hooks/useSortEmployees';
import { FormFields } from '@epi-forms/helpers';
import { employeesPerPage } from '@epi-constants/topupEmployees';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';

import {
  BreakWordCell,
  HeaderButton,
  NoWrapHeader,
  Summary
} from './EmployeesTable.styles';
import { SelectBenefitGroups } from './SelectBenefitGroups/SelectBenefitGroups';

export const EmployeesTable = ({
  toggleEmployee,
  paginationCount,
  checkedCount,
  toggleAllEmployee,
  isAllEmployeeChecked,
  isLunchTopupRequest
}: IEmployeesTable) => {
  const { t } = useTranslation();
  const language = useSelector(getApplicationLanguageId);

  const { trigger, watch, setValue } = useFormContext();
  const { gray50, fontSemiBold, gray10, gray85 } = useTheme();
  const { handleSort, descending, sortBy } = useSortEmployees();
  const { data } = useGetBenefitGroupsQuery();

  const sortingIcon = descending ? <SortDownIcon /> : <SortUpIcon />;
  const minWidth = language === 'en' ? 66 : 140;
  const page = watch(FormFields.Page);
  const checkableEmployees = watch(FormFields.CheckableEmployees);
  const daysToTopup = watch(FormFields.DaysForLunari);
  const benefitType = watch(FormFields.Benefit);
  const isMoreThanOneBenefitGroup = data && data.length > 1;

  const visibleEmployees = checkableEmployees.slice(
    page * employeesPerPage - employeesPerPage,
    page * employeesPerPage
  );

  const selectBenefitGroupsOptions = data
    ?.filter(
      item =>
        item.beneficiaryCount > 0 &&
        item.benefitSettings.some(x => x.benefitType === benefitType)
    )
    .map(item => ({
      label: item.groupName,
      value: item.groupId
    })) as IAutocompleteOption[];

  const handleToggleEmployee = employee => {
    toggleEmployee(employee);
    trigger();
  };

  const handleToggleAllEmployee = () => {
    toggleAllEmployee(!isAllEmployeeChecked);
    trigger();
  };

  return (
    <Box marginTop="40px">
      {isMoreThanOneBenefitGroup && (
        <SelectBenefitGroups options={selectBenefitGroupsOptions} />
      )}
      <Table id="employee-table">
        <Table.Head>
          <Table.Row>
            <Table.Cell>
              <Box display="flex" alignItems="center" gap={2}>
                <Checkbox
                  id="employee-table-checkbox"
                  noPadding
                  onClick={handleToggleAllEmployee}
                  checked={isAllEmployeeChecked}
                />
                <HeaderButton
                  onClick={() => handleSort(TopupSorting.ByFirstName)}
                  id="employee-table-name"
                >
                  {t('containers.deposit_money.topup_tab.form.table.name')}
                  {sortBy === TopupSorting.ByFirstName && sortingIcon}
                </HeaderButton>
              </Box>
            </Table.Cell>
            <Table.Cell id="employee-table-email">
              {t('containers.deposit_money.topup_tab.form.table.email')}
            </Table.Cell>
            <Table.Cell id="employee-table-employee-type">
              {t('containers.deposit_money.topup_tab.form.table.employee_type')}
            </Table.Cell>
            {isMoreThanOneBenefitGroup && (
              <Table.Cell id="employee-table-benefit-group">
                {t(
                  'containers.deposit_money.topup_tab.form.table.benefit_group'
                )}
              </Table.Cell>
            )}
            {benefitType === BenefitType.lunch && (
              <>
                {!isLunchTopupRequest && (
                  <Table.Cell id="employee-table-remaining-days">
                    {t(
                      'containers.deposit_money.topup_tab.form.table.days_remaining'
                    )}
                  </Table.Cell>
                )}
                <NoWrapHeader id="employee-table-load-value">
                  {isLunchTopupRequest
                    ? t(
                        'containers.deposit_money.topup_tab.form.table.daily_amount'
                      )
                    : t(
                        'containers.deposit_money.topup_tab.form.table.load_value'
                      )}
                </NoWrapHeader>
              </>
            )}
            {benefitType === BenefitType.lunch ? (
              <NoWrapHeader id="employee-table-total">
                {isLunchTopupRequest
                  ? t(
                      'containers.deposit_money.topup_tab.form.table.load_amount'
                    )
                  : t('containers.deposit_money.topup_tab.form.table.total')}
              </NoWrapHeader>
            ) : (
              <Table.Cell
                width="15%"
                align="right"
                id="employee-table-topup-left"
              >
                <HeaderButton
                  onClick={() =>
                    handleSort(TopupSorting.ByAvailableToTopupAmount)
                  }
                >
                  {sortBy === TopupSorting.ByAvailableToTopupAmount &&
                    sortingIcon}

                  {t(
                    'containers.deposit_money.topup_tab.form.table.topup_left'
                  )}
                </HeaderButton>
              </Table.Cell>
            )}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {visibleEmployees.map(employee => (
            <Table.Row
              key={employee.beneficiaryId}
              disabled={employee.disabled}
              id={`employee-table-row-${employee.personalId}`}
              onHold={employee.onHold}
            >
              <Table.Cell>
                <Box display="flex" alignItems="center">
                  <Box paddingRight="16px">
                    <Checkbox
                      id={`employee-table-checkbox-${employee.personalId}`}
                      noPadding
                      checked={employee.isChecked}
                      onChange={() => handleToggleEmployee(employee)}
                      disabled={employee.disabled}
                    />
                  </Box>
                  <div id={`employee-table-name-${employee.personalId}`}>
                    {employee.firstName} {employee.lastName}
                  </div>
                  {employee.onHold && (
                    <Box
                      id={`employee-table-onhold-label-${employee.personalId}`}
                      ml={3}
                      display="flex"
                      minWidth={minWidth}
                      height={16}
                      alignItems="center"
                      justifyContent="center"
                      color={gray85}
                      fontSize={11.5}
                      borderRadius={1.25}
                      textTransform="uppercase"
                      fontWeight={fontSemiBold}
                      bgcolor={gray10}
                      border="1px solid"
                      borderColor={gray50}
                    >
                      {t(
                        'containers.deposit_money.topup_tab.form.table.on_hold_label'
                      )}
                    </Box>
                  )}
                </Box>
              </Table.Cell>
              <BreakWordCell>{employee.emailAddress}</BreakWordCell>
              <Table.Cell>
                {t(
                  `containers.deposit_money.topup_tab.form.table.${employee.employmentType}`,
                  { percentage: employee.benefitPercentage }
                )}
              </Table.Cell>
              {isMoreThanOneBenefitGroup && (
                <Table.Cell>{employee.groupName}</Table.Cell>
              )}
              {benefitType === BenefitType.lunch && (
                <>
                  {!isLunchTopupRequest && (
                    <Table.Cell>
                      {employee.remainingDays}{' '}
                      {t('containers.deposit_money.topup_tab.form.table.days')}
                    </Table.Cell>
                  )}
                  <Table.Cell align="right">
                    {formatCurrencyDecimal(employee.loadValue)}
                  </Table.Cell>
                </>
              )}
              {benefitType === BenefitType.lunch ? (
                <Table.Cell align="right">
                  {formatCurrencyDecimal(
                    employee.remainingDays < daysToTopup
                      ? employee.remainingDays * employee.loadValue
                      : daysToTopup * employee.loadValue
                  )}
                </Table.Cell>
              ) : (
                <Table.Cell align="right">
                  {formatCurrencyDecimal(employee.availableToTopUpAmount)}
                </Table.Cell>
              )}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      <Summary id="employee-table-summary">
        <span>
          {t('containers.deposit_money.topup_tab.form.table.selected')}
        </span>
        <span>{checkedCount}</span>
      </Summary>
      <Box mt={3}>
        <Pagination
          page={page}
          count={paginationCount}
          defaultRowsPerPage={employeesPerPage}
          onChangePage={pageNumber => setValue(FormFields.Page, pageNumber)}
        />
      </Box>
    </Box>
  );
};
